import React, { useEffect, useState } from 'react';
import { userApi } from '../services/api';

interface UserRecord {
  id: number;
  username: string;
  email: string;
  level: number | null;
  active: boolean | null;
}

interface UserForm {
  username: string;
  password: string;
  email: string;
  level: number;
  active: boolean;
}

const emptyForm: UserForm = { username: '', password: '', email: '', level: 1, active: true };

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [error, setError] = useState('');

  const loadUsers = () => {
    setLoading(true);
    userApi.list()
      .then((res) => setUsers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const handleNew = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const handleEdit = (user: UserRecord) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      password: '',
      email: user.email,
      level: user.level ?? 1,
      active: user.active ?? true,
    });
    setError('');
    setShowForm(true);
  };

  const handleDelete = async (user: UserRecord) => {
    if (!window.confirm(`Delete user "${user.username}"?`)) return;
    try {
      await userApi.delete(user.id);
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingUser) {
        const updateData: any = {
          email: form.email,
          level: form.level,
          active: form.active,
        };
        if (form.password) {
          updateData.password = form.password;
        }
        await userApi.update(editingUser.id, updateData);
      } else {
        if (!form.username || !form.password || !form.email) {
          setError('Username, password, and email are required');
          return;
        }
        await userApi.create(form);
      }
      setShowForm(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Operation failed');
    }
  };

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>User Management</h4>

      <div style={styles.toolbar}>
        <input
          type="button"
          value="New User"
          onClick={handleNew}
          style={styles.button}
        />
      </div>

      {showForm && (
        <div style={styles.formWrapper}>
          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>
              {editingUser ? `Edit User: ${editingUser.username}` : 'New User'}
            </legend>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <ol style={styles.fieldList}>
                {!editingUser && (
                  <li style={styles.field}>
                    <label style={styles.label}>Username:</label>
                    <input
                      type="text"
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </li>
                )}
                <li style={styles.field}>
                  <label style={styles.label}>
                    {editingUser ? 'New Password:' : 'Password:'}
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    style={styles.input}
                    required={!editingUser}
                    placeholder={editingUser ? '(leave blank to keep)' : ''}
                  />
                </li>
                <li style={styles.field}>
                  <label style={styles.label}>Email:</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    style={styles.input}
                    required
                  />
                </li>
                <li style={styles.field}>
                  <label style={styles.label}>Level:</label>
                  <input
                    type="number"
                    value={form.level}
                    onChange={(e) => setForm({ ...form, level: parseInt(e.target.value) || 1 })}
                    style={{ ...styles.input, width: '5em' }}
                  />
                </li>
                <li style={styles.field}>
                  <label style={styles.label}>Active:</label>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                </li>
              </ol>
              <fieldset style={styles.controls}>
                <input type="submit" value={editingUser ? 'Update' : 'Create'} style={styles.button} />
                <input
                  type="button"
                  value="Cancel"
                  onClick={() => setShowForm(false)}
                  style={{ ...styles.button, marginLeft: '8px' }}
                />
              </fieldset>
            </form>
          </fieldset>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Username</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Level</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, idx) => {
              const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
              return (
                <tr key={u.id} style={{ backgroundColor: rowBg }}>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{u.id}</td>
                  <td style={styles.td}><strong>{u.username}</strong></td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{u.level}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    {u.active ? 'Yes' : 'No'}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <a href="#edit" onClick={(e) => { e.preventDefault(); handleEdit(u); }} style={styles.actionLink}>
                      Edit
                    </a>
                    {' | '}
                    <a href="#delete" onClick={(e) => { e.preventDefault(); handleDelete(u); }} style={styles.actionLink}>
                      Delete
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '1em 0.5em',
    backgroundColor: '#fff',
    minHeight: 'calc(100vh - 6em)',
  },
  heading: {
    color: '#0069cc',
    marginTop: '1em',
    textAlign: 'center',
  },
  toolbar: {
    padding: '0.5em 0',
    textAlign: 'right',
  },
  formWrapper: {
    maxWidth: '36em',
    margin: '1em auto',
  },
  fieldset: {
    border: '1px solid #808080',
    backgroundColor: '#fff',
    padding: '0.8em 1em 0',
  },
  legend: {
    fontWeight: 'bold',
    marginLeft: '0.5em',
    padding: '0 0.2em',
  },
  error: {
    border: '1px dotted #700',
    color: '#a00',
    padding: '0.5em 1em',
    marginBottom: '0.5em',
  },
  fieldList: {
    listStyle: 'none',
    padding: '0.8em 1em 0',
    margin: 0,
  },
  field: {
    paddingBottom: '0.8em',
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    width: '9em',
    textAlign: 'right' as const,
    marginRight: '1em',
  },
  input: {
    width: '15em',
    padding: '2px 4px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '1em',
  },
  controls: {
    border: 'none',
    textAlign: 'right' as const,
    padding: '0.3em 1em 1em',
  },
  button: {
    padding: '4px 16px',
    backgroundColor: '#ccdcfc',
    color: '#000',
    border: '1px solid #808080',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    fontSize: '1em',
  },
  table: {
    width: '100%',
    marginTop: '1em',
    border: '2px solid #0d58e6',
    borderCollapse: 'collapse' as const,
  },
  th: {
    border: '1px solid #0d58e6',
    padding: '4px 6px',
    backgroundColor: '#cce6ff',
    fontWeight: 'bold',
    fontSize: '0.85em',
    textAlign: 'center' as const,
  },
  td: {
    border: '1px solid #0d58e6',
    padding: '3px 6px',
    fontSize: '0.85em',
  },
  actionLink: {
    color: '#0069cc',
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: '0.9em',
  },
};

export default UsersPage;
